import { useEffect, useRef, useState } from 'react'
import Message from '@/components/ui/Message'
import Window from '@/components/ui/Window'
import { useStorage } from '@/contexts/StorageContext'
import type { ArtifactData } from '@shared/types/artifact'
import ArtifactHtmlViewer from './ArtifactHtmlViewer'

interface ArtifactWindowProps {
  artifactId: string
  windowId: number
  isActive: boolean
  onFocus: () => void
  onClose: () => void
  zIndex: number
  isMobile?: boolean
}

export default function ArtifactWindow({
  artifactId,
  windowId,
  isActive,
  onFocus,
  onClose,
  zIndex,
  isMobile = false,
}: ArtifactWindowProps) {
  const { getArtifact } = useStorage()
  const [artifact, setArtifact] = useState<ArtifactData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Track which artifact we've loaded to prevent re-fetching on context changes
  const loadedArtifactIdRef = useRef<string | null>(null)

  useEffect(() => {
    // Skip if we've already loaded this artifact
    if (loadedArtifactIdRef.current === artifactId && artifact) return

    const loadArtifact = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await getArtifact(artifactId)
        if (data) {
          setArtifact(data)
          loadedArtifactIdRef.current = artifactId
        } else {
          setError('Failed to load artifact')
        }
      } catch (err) {
        console.error('Error loading artifact:', err)
        setError('Failed to load artifact')
      } finally {
        setIsLoading(false)
      }
    }

    loadArtifact()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId]) // Intentionally omit getArtifact - we only want to fetch when artifactId changes

  return (
    <Window
      id={windowId}
      title={artifact?.name || 'Loading...'}
      isActive={isActive}
      onFocus={onFocus}
      onClose={onClose}
      zIndex={zIndex}
      initialSize={{ width: 800, height: 600 }}
      initialPosition={{ x: 100, y: 100 }}
      isMobile={isMobile}
    >
      {isLoading ? (
        <div style={{ padding: '20px' }}>
          <Message variant="loading">Loading artifact...</Message>
        </div>
      ) : error ? (
        <div style={{ padding: '20px' }}>
          <Message variant="error">{error}</Message>
        </div>
      ) : artifact ? (
        <ArtifactHtmlViewer content={artifact.content} onFocus={onFocus} />
      ) : (
        <div style={{ padding: '20px' }}>
          <Message variant="error">Artifact not found</Message>
        </div>
      )}
    </Window>
  )
}
